import type { QueryClient } from "@tanstack/react-query";

import type { CopilotResourceRef } from "src/clients/agent-management";
import type { WorkspaceDataplane } from "src/clients/flow-api";
import {
  CONTEXTUAL_ENTITY_DEFINITIONS,
  isReferenceContextualEntityType,
} from "src/unifiedCopilot/context/entityDefinitions";
import type { ReferenceContextualEntity } from "src/unifiedCopilot/context/types";
import { mapContextualEntityRefToResourceRef } from "src/unifiedCopilot/context/utils";

export type EntityResolutionContext = {
  orgId: string;
  wsId: string;
  /** The current workspace, for resolvers that need it. */
  workspace: WorkspaceDataplane;
  /** The merged mention catalogue (ambient + global) — already in memory. */
  entities: ReferenceContextualEntity[];
  /**
   * Fetch transport. Resolvers go through ensureQueryData/fetchQuery so
   * react-query owns caching and staleness — a warm cache resolves without a
   * request.
   */
  queryClient: QueryClient;
};

/**
 * Completes a ref of one entity type from its id — parent included when the
 * type has one.
 */
export type ContextualEntityResolver = (
  id: string,
  context: EntityResolutionContext,
) => Promise<CopilotResourceRef | null>;

const parentFromCatalogue = (
  ref: CopilotResourceRef,
  context: EntityResolutionContext,
): CopilotResourceRef | undefined => {
  const entity = context.entities.find(
    (candidate) => candidate.type === ref.type && candidate.id === ref.id,
  );
  return entity?.parent
    ? mapContextualEntityRefToResourceRef(entity.parent)
    : undefined;
};

const resolveParent = async (
  ref: CopilotResourceRef,
  context: EntityResolutionContext,
): Promise<CopilotResourceRef | undefined> => {
  if (ref.parent) {
    return ref.parent;
  }
  const catalogueParent = parentFromCatalogue(ref, context);
  if (catalogueParent) {
    return catalogueParent;
  }
  const resolve = isReferenceContextualEntityType(ref.type)
    ? CONTEXTUAL_ENTITY_DEFINITIONS[ref.type].resolve
    : undefined;
  return (await resolve?.(ref.id, context))?.parent ?? undefined;
};

/**
 * Completes a ref's ancestor chain by traversing upward. Each level's parent
 * comes from the ref's own chain first, then the entity catalogue (already in
 * memory), then the type's resolver (react-query-cached fetch); the recursion
 * ends at a level with no parent left to discover (chain root or dead end).
 */
export const resolveEntityRefChain = async (
  ref: CopilotResourceRef,
  context: EntityResolutionContext,
): Promise<CopilotResourceRef> => {
  const parent = await resolveParent(ref, context);
  if (!parent) {
    return { type: ref.type, id: ref.id };
  }
  return {
    type: ref.type,
    id: ref.id,
    parent: await resolveEntityRefChain(parent, context),
  };
};
